import React from 'react';
import moment from 'moment-timezone';
import throttle from 'lodash/throttle';
import { ReactStripeElements, injectStripe } from 'react-stripe-elements';
import Cookies from 'lib/Cookies';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingIsolatedViewHeader from 'coaching/components/CoachingIsolatedViewHeader';
import CoachingSelfServeFailureView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeFailureView';
import CoachingSelfServePaymentView from 'coaching/components/CoachingSelfServeView/CoachingSelfServePaymentView';
import CoachingSelfServeScheduleSessionView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeScheduleSessionView';
import CoachingSelfServeStudentDetailsView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeStudentDetailsView';
import CoachingSelfServeSuccessView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeSuccessView';
import CoachingSelfServeTransitionToSignup from 'coaching/components/CoachingSelfServeView/CoachingSelfServeTransitionToSignup';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import GetAvailableReservationsContainer, {
  CoachingReservation,
  SavedCoachingReservation,
} from 'coaching/containers/GetAvailableReservationsContainer';
import GetProspectiveStudentOptions, {
  Student,
} from 'coaching/containers/GetProspectiveStudentOptionsContainer';
import { pushCustomEvent } from 'lib/googleTagManager';
import { getNextNCoachingDays, getDatesFromReservations } from 'coaching/lib/timeIntervals';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { purchaseCoaching } from 'coaching/actions/CoachingActions';
import Row from 'components/Row';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';

import 'coaching/components/styles/CoachingIsolatedView.scss';
import { withRouter } from 'app/context/Router';
import { InjectedRouter } from 'react-router';
import {
  enableExperimentIfAppropriate,
  experimentEnabled,
  waitForABTasty,
  savePurchaseData,
  setShouldLoadFunnelTest,
  shouldLoadFunnelTest,
} from 'coaching/experiment/SignInFunnelExperiment';
import CoachingSelfServePostPurchaseView from 'coaching/components/CoachingSelfServeView/CoachingSelfServePostPurchaseView';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
// @ts-ignore
import setupCriteria from 'setup/utils/setupCriteria';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';

export type PurchaseFailureReason = 'PURCHASE_FAILURE_DUPLICATE_PURCHASE';

// exporting Props to add types to the unit test
export type Props = {
  router: InjectedRouter;
};

type State = {
  currentStep: Steps;
  selectedSubject?: string;
  selectedStudent?: Student;
  selectedGradeLevel?: string;
  manualInputChildName?: string;
  // CoachingSelfServeScheduleSessionView passes a CoachingReservation object into its callback.
  // We call reserveCoachingSession and upgrade it to a SavedCoachingReservation,
  // with reservationUuid and expiresAt properties.
  selectedReservation?: CoachingReservation | SavedCoachingReservation;
  weekOffset: number;
  userTimezone: string;
  cardInputCompleted: boolean;
  submittingPayment: boolean;
  cardFailureReason: null | string;

  // Non-Stripe purchase failures
  purchaseFailureReason?: PurchaseFailureReason;

  // this is the UUID of the participant row in r101-coaching
  // which gets created *after* a successful purchase
  participantUUID?: string;
  loadingExperiment: boolean;
};

type Steps = 'Details' | 'Schedule' | 'Complete' | 'Confirmation' | 'Success' | 'Error';
const stepsWithBreadcrumb = ['Details', 'Schedule', 'Complete', 'Confirmation'];

class CoachingSelfServeView extends React.Component<
  Props & ReactStripeElements.InjectedStripeProps,
  State
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      currentStep: 'Details',
      weekOffset: 0,
      userTimezone: TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles',
      cardInputCompleted: false,
      submittingPayment: false,
      cardFailureReason: null,
      loadingExperiment: shouldLoadFunnelTest(),
    };
  }

  componentDidMount() {
    if (this.state.loadingExperiment) {
      waitForABTasty()
        .then(() => {
          enableExperimentIfAppropriate();
          setShouldLoadFunnelTest(false);
          if (!experimentEnabled()) {
            this.props.router.replace('/sign_in');
            return;
          }
          this.setState({ loadingExperiment: false });
        })
        .catch(() => {
          setShouldLoadFunnelTest(false);
          this.props.router.replace('/sign_in');
        });
    } else {
      Cookies.expire(CoachingConstants.IS_INTENDED_COACHING_USER_COOKIE);
    }
  }

  // lock the current date/time to anchor the availability queries
  currentDate = moment().toISOString();

  isStudent = (): boolean => {
    return !experimentEnabled() && CurrentUser.getUser()?.isStudent();
  };

  getCardOwnerName = () => {
    if (experimentEnabled()) {
      return undefined;
    } else {
      return CurrentUser.getUser().getDisplayName();
    }
  };

  setCurrentStep = (step: Steps) => {
    this.setState({ currentStep: step });
  };

  getSelectedStudentInfo = (): (Partial<Student> & { name: string }) | undefined => {
    const { selectedStudent, manualInputChildName } = this.state;
    if (this.isStudent()) {
      const user = CurrentUser.getUser();
      return user ? { uuid: user.getUuid(), name: user.getName() } : undefined;
    }
    if (!selectedStudent && !manualInputChildName) {
      return undefined;
    }
    return (
      selectedStudent ?? {
        name: manualInputChildName!,
      }
    );
  };

  handleSelectReservation = (reservation: CoachingReservation) => {
    this.setState({ selectedReservation: reservation });
  };

  handleSelectStudent = (studentUuid: string, students?: Student[]) => {
    const student = students?.find(s => s.uuid === studentUuid);
    if (!student) {
      ToastActions.showErrorToast(
        'Oops - looks like something went wrong. Please try again or select a different student.'
      );
      console.error('CoachingSelfServeView - tried to select student that does not exist');
      return;
    }
    this.setState({ selectedStudent: student, manualInputChildName: undefined });
  };

  handleManuallyInputChildName = (childName?: string) => {
    this.setState({ selectedStudent: undefined, manualInputChildName: childName });
  };

  // throttled so that it can only be invoked at most once per second (avoid double clicks)
  handleSubmitPayment = throttle(async () => {
    this.setState({ submittingPayment: true, cardFailureReason: null });

    const { source, error } = await this.props.stripe!.createSource({
      type: 'card',
      owner: this.getCardOwnerName(),
    });
    if (!source) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      console.error('CoachingSelfServeView - problem tokenizing card');
      this.setState({ submittingPayment: false });
      return;
    }

    const { selectedReservation, userTimezone, selectedSubject, selectedGradeLevel } = this.state;
    if (experimentEnabled()) {
      const { selectedReservation, userTimezone, selectedSubject, selectedGradeLevel } = this.state;
      const student = {
        studentName: this.state.manualInputChildName!,
      };
      savePurchaseData({
        student,
        selectedReservation: (selectedReservation as SavedCoachingReservation)!,
        userTimezone,
        selectedSubject: selectedSubject!,
        selectedGradeLevel: selectedGradeLevel!,
        source: source.id,
      });
      this.setState({ submittingPayment: false });
      this.setCurrentStep('Confirmation');
    } else {
      const selectedStudent = this.getSelectedStudentInfo();
      if (!selectedReservation || !selectedSubject || !selectedStudent || !selectedGradeLevel) {
        ToastActions.showErrorToast(
          'Looks like you are missing some information. Please go back and fill out all of the fields before submitting payment.'
        );
        console.error('CoachingSelfServeView - tried to submit without a reservation or subject');
        this.setState({ submittingPayment: false });
        return;
      }

      const user = CurrentUser.getUser();
      return purchaseCoaching(
        selectedReservation.startDateTime.toISOString(),
        selectedReservation.endDateTime.toISOString(),
        selectedReservation.coachUuid,
        source.id,
        user.getUuid(),
        userTimezone,
        [selectedSubject],
        selectedStudent,
        selectedGradeLevel,
        (selectedReservation as SavedCoachingReservation).reservationUuid
      )
        .then(res => {
          this.setState({ submittingPayment: false });

          if (res.error?.code === 'PURCHASE_FAILURE_CARD_FAILURE') {
            this.setState({ cardFailureReason: res.error.message });
            return;
          }

          if (res.error?.code === 'PURCHASE_FAILURE_DUPLICATE_PURCHASE') {
            this.setState({ purchaseFailureReason: 'PURCHASE_FAILURE_DUPLICATE_PURCHASE' });
            this.setCurrentStep('Error');
            return;
          }

          // Any other unknown error goes to the catch-all error screen
          if (res.error || res.participantUuid === null) {
            console.warn('CoachingSelfServeView - purchase failed');
            this.setCurrentStep('Error');
            return;
          }

          // Track success in GTM via custom trigger
          pushCustomEvent('free_session_signup_completed');

          this.setState({ participantUUID: res.participantUuid });
          this.setCurrentStep('Success');
        })
        .catch(err => {
          console.warn(err);
          this.setState({ submittingPayment: false });
          this.setCurrentStep('Error');
        });
    }
  }, 1000);

  childSelectionStepIsInvalid = () => {
    return (
      !this.getSelectedStudentInfo() ||
      !this.state.selectedSubject ||
      !this.state.selectedGradeLevel
    );
  };

  scheduleSessionStepIsInvalid = () => {
    return !this.state.selectedReservation;
  };

  renderChildSelectionStep = () => {
    return (
      <GetProspectiveStudentOptions>
        {({ students, subjects, gradeLevels, loading, error }) => (
          <CoachingSelfServeStudentDetailsView
            students={students}
            subjects={subjects}
            gradeLevels={gradeLevels}
            loading={loading}
            loadingError={error?.message}
            selectedSubject={this.state.selectedSubject}
            selectedStudentUuid={this.state.selectedStudent?.uuid}
            onSelectSubject={subject => this.setState({ selectedSubject: subject })}
            onSelectStudent={uuid => this.handleSelectStudent(uuid, students)}
            selectedGradeLevel={this.state.selectedGradeLevel}
            onSelectGradeLevel={gradeLevel => this.setState({ selectedGradeLevel: gradeLevel })}
            onChangeName={this.handleManuallyInputChildName}
            onContinue={() => this.setCurrentStep('Schedule')}
            disabledContinueButton={this.childSelectionStepIsInvalid()}
            manualInputChildName={this.state.manualInputChildName}
            isStudent={this.isStudent()}
          />
        )}
      </GetProspectiveStudentOptions>
    );
  };

  renderScheduleSessionStep = () => {
    const { selectedSubject, weekOffset, userTimezone } = this.state;
    if (!selectedSubject) {
      return (
        <ErrorReporter error="CoachingSelfServeView - tried to render schedule session without a subject">
          Oops! Looks like you haven't chosen a math subject. Please go back and select one.
        </ErrorReporter>
      );
    }
    const dateTimeRange = getNextNCoachingDays(6, weekOffset, userTimezone, this.currentDate);
    const startDateTimeRange = dateTimeRange[0]?.toISOString();
    const endDateTimeRange = dateTimeRange
      .slice(-1)
      .pop()
      ?.toISOString();
    if (!startDateTimeRange || !endDateTimeRange) {
      return (
        <ErrorReporter error="CoachingSelfServeView - tried to render schedule session without a datetime range">
          Oops! Looks like something went wrong. Please try refreshing the page.
        </ErrorReporter>
      );
    }

    return (
      <GetAvailableReservationsContainer
        subject={selectedSubject}
        startDateTimeRange={startDateTimeRange}
        endDateTimeRange={endDateTimeRange}>
        {({ reservations, timeOff, error, loading }) => {
          // We may get available reservation slots on non-consecutive days with gaps between reservations that
          // can span week due to holidays, so just get the dates the reservations are on so that the UI can show
          // all the dates the reservations are on.
          const daysToShow = getDatesFromReservations(
            reservations?.map(x => x.startDateTime) ?? dateTimeRange
          );

          return (
            <CoachingSelfServeScheduleSessionView
              selectedSubject={selectedSubject}
              userTimezone={this.state.userTimezone}
              onSelectTimezone={tz => this.setState({ userTimezone: tz })}
              selectedReservation={this.state.selectedReservation}
              onSelectReservation={this.handleSelectReservation}
              onContinue={() => this.setCurrentStep('Complete')}
              disabledContinueButton={this.scheduleSessionStepIsInvalid()}
              daysToShow={daysToShow}
              reservations={reservations}
              timeOff={timeOff}
              loading={loading}
              error={error?.message}
            />
          );
        }}
      </GetAvailableReservationsContainer>
    );
  };

  renderPaymentStep = () => {
    const {
      selectedStudent,
      selectedReservation,
      userTimezone,
      cardInputCompleted,
      submittingPayment,
      cardFailureReason,
    } = this.state;

    return (
      <CoachingSelfServePaymentView
        onCardInputCompleted={() => this.setState({ cardInputCompleted: true })}
        childName={this.getSelectedStudentInfo()?.name}
        purchaseForConnectedChild={selectedStudent ? true : false}
        reservation={selectedReservation}
        userTimezone={userTimezone}
        disabledContinueButton={!cardInputCompleted}
        onSubmitPayment={this.handleSubmitPayment}
        submittingPayment={submittingPayment}
        isStudent={this.isStudent()}
        cardFailureReason={cardFailureReason}
        selectedSubject={this.state.selectedSubject}
      />
    );
  };

  renderSuccess = () => {
    const user = CurrentUser.getUser();
    const student = this.getSelectedStudentInfo();
    const hasPostPurchaseFlowFlag = user.hasFeature('coaching_connect_student_account.web.nov-20');
    const purchasingForUnconnectedStudent = !student?.uuid;
    const Component =
      hasPostPurchaseFlowFlag && purchasingForUnconnectedStudent
        ? CoachingSelfServePostPurchaseView
        : CoachingSelfServeSuccessView;
    return (
      <Component
        childName={student?.name}
        reservation={this.state.selectedReservation}
        userTimezone={this.state.userTimezone}
        userEmail={user.getEmail()}
        childEmail={student?.email}
        participantUUID={this.state.participantUUID}
        selectedSubject={this.state.selectedSubject}
      />
    );
  };

  renderTransitionToSignUp = () => {
    const { selectedReservation, selectedSubject, userTimezone } = this.state;
    return (
      <CoachingSelfServeTransitionToSignup
        userTimezone={userTimezone}
        childName={this.getSelectedStudentInfo()?.name}
        subject={selectedSubject}
        reservation={selectedReservation}
        onSignIn={() => {
          this.props.router.push('/sign_in');
        }}
      />
    );
  };

  renderCurrentStep = () => {
    const mapStepToComponent: { [Key in Steps]: () => React.ReactNode } = {
      Details: this.renderChildSelectionStep,
      Schedule: this.renderScheduleSessionStep,
      Complete: this.renderPaymentStep,
      Confirmation: this.renderTransitionToSignUp,
      Error: () => (
        <CoachingSelfServeFailureView failureReason={this.state.purchaseFailureReason} />
      ),
      Success: this.renderSuccess,
    };

    return mapStepToComponent[this.state.currentStep]();
  };

  renderBreadcrumbs = () => {
    const chevron = <Icon size="small" name="chevron-right" color="graphite" />;
    const childSelectionStepIsInvalid = this.childSelectionStepIsInvalid();
    const scheduleSessionStepIsInvalid = this.scheduleSessionStepIsInvalid();
    return (
      <Row>
        <div className="CoachingIsolatedView-breadcrumbs">
          {this.renderCrumb('Details', () => this.setCurrentStep('Details'), 'Details')}
          {chevron}
          {this.renderCrumb(
            'Schedule',
            () => this.setCurrentStep('Schedule'),
            'Schedule',
            () => childSelectionStepIsInvalid
          )}
          {chevron}
          {this.renderCrumb(
            'Complete',
            () => this.setCurrentStep('Complete'),
            'Complete',
            () => childSelectionStepIsInvalid || scheduleSessionStepIsInvalid
          )}
          {experimentEnabled() && chevron}
          {experimentEnabled() &&
            this.renderCrumb(
              'Confirmation',
              () => this.setCurrentStep('Confirmation'),
              'Confirmation',
              () => childSelectionStepIsInvalid || scheduleSessionStepIsInvalid
            )}
        </div>
      </Row>
    );
  };

  renderCrumb = (
    name: string,
    onClick: () => void,
    stepName: Steps,
    disabledCondition?: () => boolean
  ) => {
    const textColor = this.state.currentStep === stepName ? 'ink' : 'graphite';
    return (
      <Link
        color={textColor}
        onClick={onClick}
        disabled={disabledCondition ? disabledCondition() : false}>
        {name}
      </Link>
    );
  };

  render() {
    if (this.state.loadingExperiment) {
      return (
        <div className="CoachingIsolatedView">
          <CoachingIsolatedViewHeader />
          <CenteredLoadingIndicator />
        </div>
      );
    } else {
      const { currentStep } = this.state;
      const showBreadcrumb = stepsWithBreadcrumb.includes(currentStep);
      return (
        <div className="CoachingIsolatedView">
          <CoachingIsolatedViewHeader />
          <Container size="medium">
            {showBreadcrumb ? this.renderBreadcrumbs() : <Row size="xxxxxlarge"> </Row>}
            {this.renderCurrentStep()}
          </Container>
        </div>
      );
    }
  }
}

export { CoachingSelfServeView };

export default injectStripe(withRouter(CoachingSelfServeView));
